import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SuiBox from "components/sui/SuiBox";
import SuiTypography from "components/sui/SuiTypography";

// Soft UI Dashboard PRO React base styles
import typography from "assets/theme/base/typography";

// Custom styles for the CdxSnackbar
import CdxSnackbarIconRoot from "components/cdx/CdxSnackbars/CdxSnackbar/CdxSnackbarIconRoot";

import Slide from "@mui/material/Slide";

import { connect } from "react-redux";
import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { destroySnackbar } from "actions/app";

function CdxSnackbar({
  color,
  icon,
  title,
  timestamp,
  content,
  close,
  bgWhite,
  uuid,
  timeout = 1000 * 10,
  destroySnackbar,
  ...rest
}) {
  const { t } = useTranslation();
  const { size } = typography;
  let titleColor;
  let dateTimeColor;
  let dividerColor;

  if (bgWhite) {
    titleColor = color;
    dateTimeColor = "dark";
    dividerColor = false;
  } else if (color === "light") {
    titleColor = "dark";
    dateTimeColor = "text";
    dividerColor = false;
  } else {
    titleColor = "white";
    dateTimeColor = "white";
    dividerColor = true;
  }

  const [dateTime, setDateTime] = useState(t("just now"));

  const handleClose = () => {
    destroySnackbar(uuid);
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      destroySnackbar(uuid);
    }, timestamp + timeout - Date.now());

    return () => {
      clearTimeout(timeOutId);
    };
  }, [destroySnackbar, uuid, timestamp, timeout]);

  useEffect(() => {
    const renderTime = () => {
      const now = Date.now();
      const timePass = (now - timestamp) / 1000;

      if (timePass + 1 < 60) {
        return {
          text: `${parseInt(timePass + 1)} ${t("sec ago")}`,
          next: timestamp + parseInt(timePass + 1) * 1000 - now + 200,
        };
      } else {
        return {
          text: `${parseInt(timePass / 60 + 1)} ${t("min ago")}`,
          next: timestamp + parseInt(timePass / 60 + 1) * 60 * 1000 - now + 200,
        };
      }
    };

    const time = renderTime();
    const timeOutId = setTimeout(() => {
      setDateTime(time.text);
    }, time.next);

    return () => {
      clearTimeout(timeOutId);
    };
  }, [dateTime, timestamp]);

  return (
    <Slide direction="up" in={true} mountOnEnter unmountOnExit>
      <SuiBox
        variant={bgWhite ? "contained" : "gradient"}
        bgColor={bgWhite ? "white" : color}
        minWidth="21.875rem"
        maxWidth="100%"
        shadow="md"
        borderRadius="md"
        p={1}
      >
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="dark"
          p={1.5}
        >
          <SuiBox display="flex" alignItems="center" lineHeight={0}>
            <CdxSnackbarIconRoot
              fontSize="small"
              ownerState={{ color, bgWhite }}
            >
              {icon}
            </CdxSnackbarIconRoot>
            <SuiTypography
              variant="button"
              fontWeight="medium"
              color={titleColor}
              textGradient={bgWhite}
            >
              {t(title)}
            </SuiTypography>
          </SuiBox>
          <SuiBox display="flex" alignItems="center" lineHeight={0} pl={1}>
            <SuiTypography variant="caption" color={dateTimeColor}>
              {dateTime}
            </SuiTypography>
            <Icon
              sx={{
                color: ({ palette: { dark, white } }) =>
                  bgWhite || color === "light" ? dark.main : white.main,
                fontWeight: ({ typography: { fontWeightBold } }) =>
                  fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
                transform: "translateY(-1px)",
              }}
              onClick={handleClose}
            >
              close
            </Icon>
          </SuiBox>
        </SuiBox>
        <Divider sx={{ margin: 0 }} light={dividerColor} />
        <SuiBox
          p={1.5}
          color={bgWhite || color === "light" ? "text" : "white"}
          fontSize={size.sm}
        >
          {t(content)}
        </SuiBox>
      </SuiBox>
    </Slide>
  );
}

// Setting default values for the props of CdxSnackbar
CdxSnackbar.defaultProps = {
  bgWhite: false,
  color: "info",
};

// Typechecking props for CdxSnackbar
CdxSnackbar.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  timestamp: PropTypes.number,
  content: PropTypes.node.isRequired,
  bgWhite: PropTypes.bool,
};

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = { destroySnackbar };
export default connect(mapStateToProps, mapDispatchToProps)(CdxSnackbar);
